import type {
  InsightsHeadcountFragment,
  InsightsInsightsPageQuery,
  InsightsJoinerFragment,
  InsightsLeaverFragment,
} from 'generated/resources/graphql'

import { CycleDateRange } from 'modules/insights/hooks/useCycleDateRange'

import AgeRangeDistribution from './metrics/ageRangeDistribution'
import EmployeesAverageAge from './metrics/employeesAverageAge'
import EmployeesPerLegalEntity from './metrics/employeesPerLegalEntity'
import { Export, GenerateExport } from './metrics/export'
import { HeadcountByGender } from './metrics/headcountByGender'
import HeadcountByTeam from './metrics/headcountByTeam'
import HeadcountEvolution from './metrics/headcountEvolution'
import PromotionsByTeam from './metrics/promotionsByTeam'
import Retention from './metrics/retention'
import Turnover from './metrics/turnover'

type CompaniesEmployeesPerLegalEntity =
  InsightsInsightsPageQuery['companies']['employeesPerLegalEntitiesConnection']['nodes'][number]
type EmployeesGenderHeadcount =
  InsightsInsightsPageQuery['employees']['genderHeadcountsConnection']['nodes'][number]
type EmployeesTeamHeadcount =
  InsightsInsightsPageQuery['employees']['teamHeadcountsConnection']['nodes'][number]
type EmployeesAgeHeadcount =
  InsightsInsightsPageQuery['employees']['ageHeadcountsConnection']['nodes'][number]
type ContractsPromotion =
  InsightsInsightsPageQuery['contracts']['promotionsConnection']['nodes'][number]
type ContractsPromotionStat =
  InsightsInsightsPageQuery['contracts']['promotionStatsConnection']['nodes'][number]

export type WidgetMetricsType = {
  headcountEvolution: HeadcountEvolution
  retention: Retention
  turnover: Turnover
  employeesPerLegalEntity: EmployeesPerLegalEntity
  headcountByGender: HeadcountByGender
  employeesAverageAge: EmployeesAverageAge
  headcountByTeam: HeadcountByTeam
  ageRangeDistribution: AgeRangeDistribution
  promotionsByTeam: PromotionsByTeam
}

export default class WidgetMetrics {
  private headcountEvolution: InsightsHeadcountFragment[]
  private leavers: InsightsLeaverFragment[]
  private joiners: InsightsJoinerFragment[]
  private ratesHeadcountEvolution: InsightsHeadcountFragment[]
  private ratesLeavers: InsightsLeaverFragment[]
  private ratesJoiners: InsightsJoinerFragment[]
  private employeesPerLegalEntity: CompaniesEmployeesPerLegalEntity[]
  private headcountByGender: EmployeesGenderHeadcount[]
  private headcountByTeam: EmployeesTeamHeadcount[]
  private employeesAverageAge: EmployeesAgeHeadcount[]
  private promotionsByTeam: ContractsPromotion[]
  private prevMonthTotals?: ContractsPromotionStat
  private cycleDateRange: CycleDateRange

  constructor(insightsData: InsightsInsightsPageQuery, cycleDateRange: CycleDateRange) {
    this.headcountEvolution = insightsData.contracts.monthlyHeadcounts
    this.leavers = insightsData.contracts.leavers
    this.joiners = insightsData.contracts.joiners
    this.ratesHeadcountEvolution = insightsData.rates.monthlyHeadcountsConnection.nodes
    this.ratesLeavers = insightsData.rates.leaversConnection.nodes
    this.ratesJoiners = insightsData.rates.joinersConnection.nodes
    this.employeesPerLegalEntity = insightsData.companies.employeesPerLegalEntitiesConnection.nodes
    this.headcountByGender = insightsData.employees.genderHeadcountsConnection.nodes
    this.headcountByTeam = insightsData.employees.teamHeadcountsConnection.nodes
    this.employeesAverageAge = insightsData.employees.ageHeadcountsConnection.nodes
    this.promotionsByTeam = insightsData.contracts.promotionsConnection.nodes
    this.prevMonthTotals = insightsData.contracts.promotionStatsConnection.nodes[0]
    this.cycleDateRange = cycleDateRange
  }

  export() {
    const data: Export[] = []

    Object.entries(this.generate()).forEach((element) => {
      const [_, exporter]: [string, GenerateExport] = element

      data.push(exporter.export())
    })

    return data
  }

  generate() {
    return {
      headcountEvolution: new HeadcountEvolution(
        this.headcountEvolution,
        this.leavers,
        this.joiners,
        this.cycleDateRange
      ),
      retention: new Retention(this.ratesHeadcountEvolution, this.ratesLeavers),
      turnover: new Turnover(this.ratesHeadcountEvolution, this.ratesLeavers, this.ratesJoiners),
      employeesPerLegalEntity: new EmployeesPerLegalEntity(this.employeesPerLegalEntity),
      headcountByGender: new HeadcountByGender(this.headcountByGender),
      employeesAverageAge: new EmployeesAverageAge(this.employeesAverageAge),
      headcountByTeam: new HeadcountByTeam(this.headcountByTeam),
      ageRangeDistribution: new AgeRangeDistribution(this.employeesAverageAge),
      promotionsByTeam: new PromotionsByTeam(this.promotionsByTeam, this.prevMonthTotals),
    }
  }
}
